/* eslint-disable max-lines-per-function, max-statements */
import prepareComponent from "@bluframe/grapple/prepareComponent"
import { ImageSelectorImage } from "@bluframe/mightychroma-mightytool-frontend-types"
import {
  ChangeEvent,
  MouseEvent,
  RefObject,
  useCallback,
  useState
} from "react"

import Item from "./Item"
import regenerateScreenshotMetadata from "./regenerateScreenshotMetadata"

export interface ImageAndIdentifier extends ImageSelectorImage {
  identifier: string
}

export interface Props {
  image: ImageAndIdentifier
  imageUrlRef?: RefObject<HTMLInputElement | null>
  isMultiSelect?: boolean

  onArrowLeftClick?: (id: string) => void

  onArrowRightClick?: (id: string) => void

  onImageUrlChange: (identifier: string, url: string) => void
  onSelectImage: () => void
  onSetImageAlternativeText: (
    identifier: string,
    alternativeText: string
  ) => void
  onSetImageCaption: (identifier: string, caption: string) => void
  onSetImageCredit: (identifier: string, credit: string) => void
  onRegenerateScreenshotMetadataField?: (input: {
    contentId?: number | string
    field: "alternativeText" | "caption"
    id?: string
    originalUrl: string
  }) => Promise<string | undefined>
  showCreditInput?: boolean
  showScreenshotMetadataInputs?: boolean
}

export interface ComponentProps extends Omit<
  Props,
  | "onArrowLeftClick"
  | "onArrowRightClick"
  | "onSetImageAlternativeText"
  | "onSetImageCaption"
  | "onImageUrlChange"
  | "onSelectImage"
  | "onSetImageCredit"
> {
  onArrowLeftClick?: (event: MouseEvent<HTMLButtonElement>) => void

  onArrowRightClick?: (event: MouseEvent<HTMLButtonElement>) => void

  onAlternativeTextInputChange: (
    event: ChangeEvent<HTMLTextAreaElement>
  ) => void
  onCaptionInputChange: (event: ChangeEvent<HTMLTextAreaElement>) => void
  isAlternativeTextRegenerating: boolean
  isCaptionRegenerating: boolean
  onRegenerateAlternativeTextClick?: (
    event: MouseEvent<HTMLButtonElement>
  ) => void
  onRegenerateCaptionClick?: (event: MouseEvent<HTMLButtonElement>) => void
  onCreditInputChange: (event: ChangeEvent<HTMLInputElement>) => void
  onImageUrlChange: (event: ChangeEvent<HTMLInputElement>) => void
  onInputTextClick: (event: MouseEvent) => void
  onSelectImage: () => void
}

/**
 * usePrepareComponent
 *
 * @description Prepares ImageSelector item callbacks and props for the presentational component.
 * @param {Props} props - Item data and callback handlers from the selector list.
 * @returns {ComponentProps} Prepared handlers and flags for the Item component.
 *
 */
export const usePrepareComponent = ({
  image,
  imageUrlRef,
  onArrowLeftClick: onArrowLeftClickProps,
  onImageUrlChange: onImageUrlChangeProps,
  onArrowRightClick: onArrowRightClickProps,
  onRegenerateScreenshotMetadataField: onRegenerateScreenshotMetadataFieldProps,
  onSetImageAlternativeText: onSetImageAlternativeTextProps,
  onSetImageCaption: onSetImageCaptionProps,
  onSetImageCredit: onSetImageCreditProps,
  ...props
}: Props): ComponentProps => {
  const [isAlternativeTextRegenerating, setIsAlternativeTextRegenerating] =
    useState(false)
  const [isCaptionRegenerating, setIsCaptionRegenerating] = useState(false)

  /**
   * onArrowLeftClick
   *
   * @description Handle the left arrow click event.
   * @param {MouseEvent<HTMLButtonElement>} event - Button click event.
   * @returns {void} Stops propagation and triggers previous-item navigation.
   *
   */
  const onArrowLeftClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()

      onArrowLeftClickProps?.(image.identifier)
    },
    [image.identifier, onArrowLeftClickProps]
  )

  /**
   * onArrowRightClick
   *
   * @description Handle the right arrow click event.
   * @param {MouseEvent<HTMLButtonElement>} event - Button click event.
   * @returns {void} Stops propagation and triggers next-item navigation.
   *
   */
  const onArrowRightClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation()
      onArrowRightClickProps?.(image.identifier)
    },
    [image.identifier, onArrowRightClickProps]
  )

  /**
   * onInputTextClick
   *
   * @description Handle the input text click event.
   * @param {MouseEvent} event - Generic click event from input wrappers.
   * @returns {void} Stops propagation so parent selection is not toggled.
   *
   */
  const onInputTextClick = useCallback(
    (event: MouseEvent) => event.stopPropagation(),
    []
  )

  /**
   * onCreditInputChange
   *
   * @description Set the image credit for the selected image identifier.
   * @param {ChangeEvent<HTMLInputElement>} event - Credit input change event.
   * @returns {void} Saves credit text for the current image.
   *
   */
  const onCreditInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation()
      onSetImageCreditProps(image.identifier, event.currentTarget.value)
    },
    [image.identifier, onSetImageCreditProps]
  )

  /**
   * onImageUrlChange
   *
   * @description Set the image URL for the selected image identifier.
   * @param {ChangeEvent<HTMLInputElement>} event - URL input change event.
   * @returns {void} Saves URL text for the current image.
   *
   */
  const onImageUrlChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      event.stopPropagation()
      onImageUrlChangeProps(image.identifier, event.currentTarget.value)
    },
    [image.identifier, onImageUrlChangeProps]
  )

  /**
   * onAlternativeTextInputChange
   *
   * @description Sets alternative text for the selected image identifier.
   * @param {ChangeEvent<HTMLTextAreaElement>} event - Alt text input change event.
   * @returns {void} Saves alternative text for the current image.
   *
   */
  const onAlternativeTextInputChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      event.stopPropagation()
      onSetImageAlternativeTextProps(
        image.identifier,
        event.currentTarget.value
      )
    },
    [image.identifier, onSetImageAlternativeTextProps]
  )

  /**
   * onCaptionInputChange
   *
   * @description Sets caption text for the selected image identifier.
   * @param {ChangeEvent<HTMLTextAreaElement>} event - Caption input change event.
   * @returns {void} Saves caption text for the current image.
   *
   */
  const onCaptionInputChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      event.stopPropagation()
      onSetImageCaptionProps(image.identifier, event.currentTarget.value)
    },
    [image.identifier, onSetImageCaptionProps]
  )

  /**
   * onRegenerateAlternativeTextClick
   *
   * @description Regenerates screenshot alternative text via GraphQL and applies it to the current item.
   * @param {MouseEvent<HTMLButtonElement>} event - Regenerate button click event.
   * @returns {Promise<void>} Resolves after regeneration completes and state is updated.
   *
   */
  const onRegenerateAlternativeTextClick = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) =>
      await regenerateScreenshotMetadata({
        event,
        field: "alternativeText",
        image,
        onRegenerateScreenshotMetadataField:
          onRegenerateScreenshotMetadataFieldProps,
        onSetFieldValue: onSetImageAlternativeTextProps,
        setIsRegenerating: setIsAlternativeTextRegenerating
      }),
    [
      image,
      onRegenerateScreenshotMetadataFieldProps,
      onSetImageAlternativeTextProps
    ]
  )

  /**
   * onRegenerateCaptionClick
   *
   * @description Regenerates screenshot caption via GraphQL and applies it to the current item.
   * @param {MouseEvent<HTMLButtonElement>} event - Regenerate button click event.
   * @returns {Promise<void>} Resolves after regeneration completes and state is updated.
   *
   */
  const onRegenerateCaptionClick = useCallback(
    async (event: MouseEvent<HTMLButtonElement>) =>
      await regenerateScreenshotMetadata({
        event,
        field: "caption",
        image,
        onRegenerateScreenshotMetadataField:
          onRegenerateScreenshotMetadataFieldProps,
        onSetFieldValue: onSetImageCaptionProps,
        setIsRegenerating: setIsCaptionRegenerating
      }),
    [image, onRegenerateScreenshotMetadataFieldProps, onSetImageCaptionProps]
  )

  return {
    ...props,
    image,
    imageUrlRef,
    isAlternativeTextRegenerating,
    isCaptionRegenerating,
    onArrowLeftClick: onArrowLeftClickProps ? onArrowLeftClick : undefined,
    onArrowRightClick: onArrowRightClickProps ? onArrowRightClick : undefined,
    onAlternativeTextInputChange,
    onCaptionInputChange,
    onCreditInputChange,
    onImageUrlChange,
    onInputTextClick,
    onRegenerateAlternativeTextClick: onRegenerateScreenshotMetadataFieldProps
      ? onRegenerateAlternativeTextClick
      : undefined,
    onRegenerateCaptionClick: onRegenerateScreenshotMetadataFieldProps
      ? onRegenerateCaptionClick
      : undefined
  }
}

export default prepareComponent<Props, ComponentProps>(usePrepareComponent)(
  Item
)
